import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type CommunityTemplateSortKey,
  communityTemplateSortField,
  GET_COMMUNITY_TEMPLATES,
} from '../../contracts';
import { getPublicTemplates } from '../api/community';

/**
 * The community gallery LIST read (D2.7, ARCFE-1046) — the gallery's primary
 * surface, and the last of the four hooks that had taken only their cache key
 * from `/contracts` while their body stayed app-side.
 *
 * What stays app-side at the delegate: the `FilterSettings` sentinel defaults,
 * `useQuerySpecialties`, the specialty-group lookup, the 500ms search debounce,
 * and the analytics effect that emits `trackCommunityTemplateSearched`. What
 * crosses is the resolved filter vocabulary — the only part that is data.
 *
 * The `field:operator:value` filter encoding and the `field:direction` sort
 * encoding stay HERE, not at the delegate: they are wire format, and the
 * comment block describing the accepted filter fields moved with them.
 */
export interface CommunityTemplatesQueryInput {
  sort: CommunityTemplateSortKey | undefined;
  location: string | undefined;
  specialty: string | undefined;
  category: string | undefined;
  specialtyGroups: string[] | undefined;
  /** Already debounced by the delegate — the 500ms timer is app orchestration. */
  search: string | undefined;
  page: number;
  enabled: boolean;
}

export function useQueryCommunityTemplates({
  sort,
  location,
  specialty,
  category,
  specialtyGroups,
  search,
  page,
  enabled,
}: CommunityTemplatesQueryInput) {
  return useQuery({
    retry: 3,
    enabled,
    // Key shape is byte-identical to the pre-move hook, including the property
    // name `search`. Its sibling `useQueryFormTemplates` spells the same value
    // `searchQuery` — the two really do differ, so neither was normalised.
    queryKey: [
      GET_COMMUNITY_TEMPLATES,
      {
        sort,
        location,
        specialty,
        specialtyGroups,
        category,
        search,
        page,
      },
    ],
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,

    // // filter: list of str in format as filter_field:filter_operator:value
    // search: a str to search in the title and description
    // sort: list of str in format as sort_field:sort_order
    // specialty_groups: list of specialty group names to expand search when < 12 results

    // filter_field: template_category, source_region, specialty, views_count, imports_count, country
    // filter_operator: eq, gt, lt
    // sort_field: created_at, updated_at, views_count, imports_count, title
    // sort_order: 1 (ascending), -1 (descending)
    queryFn: async () => {
      const sortParam =
        sort &&
        `${communityTemplateSortField[sort]}:${sort === 'ALPHABETICAL' ? '1' : '-1'}`;
      const locationParam = location ? `country:eq:${location}` : undefined;
      const specialtyParam = specialty ? `specialty:eq:${specialty}` : undefined;
      const categoryParam = category ? `template_category:eq:${category}` : undefined;
      const { data } = await getPublicTemplates({
        filter: [locationParam, specialtyParam, categoryParam],
        sort: sortParam,
        search,
        page,
        specialtyGroups,
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });
}
